import * as React from 'react';

import { Hexinho } from '@/componentes/hexagono';
import { cn } from '@/lib/utils';

/* SeloEstado — o julgamento em uma palavra.
 *
 * É o que diz "Abaixo do preço mínimo" no alto do veredito e "no ponto" ao
 * lado de um indicador. Difere do `Badge` do shadcn de propósito: `Badge` é
 * uma etiqueta genérica (rascunho, contagem, categoria); `SeloEstado` carrega
 * JUÍZO, e por isso tem marcador de célula e tinta semântica.
 *
 * A CAIXA ALTA SAIU, e não sobrou opção de ligá-la de volta. Era o último
 * resto do rótulo antigo, e aqui doía mais que em qualquer outro lugar: o selo
 * do veredito não é etiqueta de uma palavra, é uma frase ("Abaixo do preço
 * mínimo"), e frase em caixa alta com entreletra aberta perde a silhueta
 * justamente no item que devia ser lido primeiro. O peso 600 e a tinta
 * semântica já separam o selo do texto ao redor.
 *
 * Ele não conhece os estados do motor. Quem traduz `recomendacao.estado` em
 * tom e texto é a tela: um componente de desenho que soubesse o que é
 * "meta-inviavel" seria regra de negócio escondida na camada errada.
 *
 * A cor se inverte sozinha dentro de uma célula `campo="mata"`: as tintas
 * semânticas trocam para as versões claras no escopo [data-campo='mata']. */

export type TomSelo = 'positivo' | 'atencao' | 'negativo' | 'info' | 'neutro';

export type TamanhoSelo = 'pequeno' | 'medio';

const TONS: Record<TomSelo, { fundo: string; tinta: string }> = {
  positivo: { fundo: 'bg-positivo-tinta', tinta: 'text-positivo' },
  atencao: { fundo: 'bg-atencao-tinta', tinta: 'text-atencao' },
  negativo: { fundo: 'bg-negativo-tinta', tinta: 'text-negativo' },
  info: { fundo: 'bg-info-tinta', tinta: 'text-info' },
  neutro: { fundo: 'bg-lavado', tinta: 'text-tinta-2' },
};

export type SeloEstadoProps = React.ComponentProps<'span'> & {
  tom?: TomSelo | undefined;
  tamanho?: TamanhoSelo | undefined;
  /** A célula à esquerda do texto. Desligue em selo dentro de tabela densa. */
  marcador?: boolean | undefined;
};

export function SeloEstado({
  tom = 'neutro',
  tamanho = 'medio',
  marcador = true,
  className,
  children,
  ...resto
}: SeloEstadoProps) {
  const cores = TONS[tom];
  return (
    <span
      data-slot="selo-estado"
      data-tom={tom}
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1.5 rounded-pilula font-semibold whitespace-nowrap',
        tamanho === 'pequeno' ? 'px-2 py-0.5 text-micro' : 'py-1 pr-3 pl-2.5 text-nota',
        cores.fundo,
        cores.tinta,
        className,
      )}
      {...resto}
    >
      {marcador ? <Hexinho tamanho={tamanho === 'pequeno' ? 8 : 9} /> : null}
      {children}
    </span>
  );
}
